import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { BRAND_ADDON_PRICE } from "@/data/siteData";
import { toast } from "sonner";
import { createPaymentOrderUSD, verifyPaymentUSD } from "@/lib/api";
import { CheckCircle2, Loader2, CreditCard, Globe2, ArrowUpRight } from "lucide-react";

const RAZORPAY_SCRIPT = "https://checkout.razorpay.com/v1/checkout.js";

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const existing = document.querySelector(`script[src="${RAZORPAY_SCRIPT}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(true));
      existing.addEventListener("error", () => resolve(false));
      return;
    }
    const script = document.createElement("script");
    script.src = RAZORPAY_SCRIPT;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

const PARTICIPATION_OPTIONS = [
  { value: "In Person - Leader Participation", label: "In Person - Leader Participation", price: 299, link: "/tech-leaders#in-person-leader", note: "In-person attendance · Leader feature & felicitation · Networking · Gala Dinner" },
  { value: "Remote - Leader Participation", label: "Remote - Leader Participation", price: 199, link: "/tech-leaders#remote-leader", note: "Featured in the summit collateral · Digital access · No on-site attendance" },
  { value: "In Person - Delegate only", label: "In Person - Delegate only", price: 99, note: "In-person delegate access · Sessions · Networking" },
];

const ADDON_OPTIONS = ["In Person - Leader Participation"];

const usd = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

const INITIAL = {
  full_name: "",
  email: "",
  phone: "",
  organization: "",
  role: "",
  pass_option: "",
};

export default function InternationalPay() {
  const [params] = useSearchParams();
  const [form, setForm] = useState(() => {
    const opt = params.get("option");
    const valid = PARTICIPATION_OPTIONS.some((p) => p.value === opt);
    return { ...INITIAL, pass_option: valid ? opt : "" };
  });
  const [brandAddon, setBrandAddon] = useState(params.get("addon") === "1");
  const addonAllowed = ADDON_OPTIONS.includes(form.pass_option);
  const withAddon = brandAddon && addonAllowed;
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const total = useMemo(() => {
    const opt = PARTICIPATION_OPTIONS.find((p) => p.value === form.pass_option);
    return opt ? opt.price + (withAddon ? BRAND_ADDON_PRICE : 0) : 0;
  }, [form.pass_option, withAddon]);

  const payNow = async () => {
    if (submitting) return;
    if (!form.full_name || !form.email || !form.phone || !form.organization || !form.role) {
      toast.error("Please fill in all required fields.");
      return;
    }
    if (!form.pass_option) {
      toast.error("Please select a participation option.");
      return;
    }

    setSubmitting(true);
    try {
      const scriptOk = await loadRazorpayScript();
      if (!scriptOk) {
        toast.error("Could not load payment gateway. Check your connection and try again.");
        return;
      }

      const order = await createPaymentOrderUSD({
        full_name: form.full_name,
        email: form.email,
        pass_option: form.pass_option,
        brand_addon: withAddon,
      });

      await new Promise((resolve) => {
        const rzp = new window.Razorpay({
          key: order.key_id,
          amount: order.amount,
          currency: order.currency,
          order_id: order.order_id,
          name: "BoT Summit 2026",
          description: `International Participation — ${form.pass_option}${withAddon ? " + Brand add-on" : ""}`,
          prefill: {
            name: form.full_name,
            email: form.email,
            contact: form.phone,
          },
          theme: { color: "#002FA7" },
          notes: {
            organization: form.organization,
            role: form.role,
            ticket_type: "delegate-intl",
          },
          modal: {
            ondismiss: () => {
              toast.message("Payment cancelled. Your details have not been saved.");
              resolve();
            },
          },
          handler: async (response) => {
            try {
              await verifyPaymentUSD({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                full_name: form.full_name,
                email: form.email,
                phone: form.phone,
                organization: form.organization,
                role: form.role,
                pass_option: form.pass_option,
              });
              toast.success("Payment successful! Your seat is confirmed.");
              setDone(true);
              setForm(INITIAL);
              setBrandAddon(false);
            } catch (verifyErr) {
              const vmsg = verifyErr?.response?.data?.detail || "Payment verification failed. Please contact support.";
              toast.error(typeof vmsg === "string" ? vmsg : "Verification failed.");
            } finally {
              resolve();
            }
          },
        });

        rzp.on("payment.failed", (resp) => {
          const reason = resp?.error?.description || "Payment failed. Please try again.";
          toast.error(reason);
        });

        rzp.open();
      });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Could not submit.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white min-h-screen">
      <header className="border-b border-zinc-200 bg-white sticky top-0 z-50 backdrop-blur-xl bg-white/90">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <span className="w-9 h-9 bg-[#002FA7] text-white font-display text-sm flex items-center justify-center">
              BoT
            </span>
            <div className="hidden sm:block leading-tight">
              <div className="font-display text-base tracking-tight">BoT Summit 2026</div>
              <div className="overline text-[10px] text-zinc-500 mt-1">Best of Tech</div>
            </div>
          </Link>
          <Link
            to="/"
            data-testid="intl-pay-back"
            className="overline text-xs text-zinc-600 hover:text-black"
          >
            ← Back to site
          </Link>
        </div>
      </header>

      <section className="border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="grid grid-cols-12 gap-y-10 lg:gap-16">
            <div className="col-span-12 lg:col-span-5">
              <div className="overline text-[#FF2A00] mb-4 flex items-center gap-2">
                <Globe2 size={14} /> / International Payment (USD)
              </div>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl uppercase tracking-tighter">
                Register from
                <br />
                <span className="text-[#002FA7]">anywhere.</span>
              </h1>
              <p className="mt-6 text-zinc-700">
                For international delegates paying in US Dollars. Secure checkout
                powered by Razorpay International.
              </p>

              <div className="mt-10 border border-zinc-200 p-6 bg-zinc-50">
                <div className="overline text-zinc-500 mb-3">Three Ways to Participate</div>
                <div className="space-y-4 text-sm">
                  {PARTICIPATION_OPTIONS.map((opt, i) => (
                    <div
                      key={opt.value}
                      data-testid={`intl-option-summary-${i}`}
                      className={`flex items-baseline justify-between gap-4 ${i ? "border-t border-zinc-200 pt-4" : ""}`}
                    >
                      <div>
                        {opt.link ? (
                          <Link
                            to={opt.link}
                            data-testid={`intl-option-link-${i}`}
                            className="group inline-flex items-center gap-1 font-display text-base uppercase tracking-tight text-black hover:text-[#002FA7] underline decoration-zinc-300 underline-offset-4 hover:decoration-[#002FA7] transition-colors"
                          >
                            {opt.label}
                            <ArrowUpRight size={14} className="shrink-0 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                          </Link>
                        ) : (
                          <div className="font-display text-base uppercase tracking-tight text-black">{opt.label}</div>
                        )}
                        <div className="text-xs text-zinc-500 mt-1">{opt.note}</div>
                      </div>
                      <div className="font-display text-2xl text-[#002FA7] shrink-0">${opt.price}</div>
                    </div>
                  ))}
                  <div
                    data-testid="intl-option-summary-addon"
                    className="border-t border-dashed border-zinc-300 pt-4 flex items-baseline justify-between gap-4"
                  >
                    <div>
                      <div className="font-display text-base uppercase tracking-tight text-black">
                        Brand add-on <span className="overline text-[#FF2A00] ml-1">Optional</span>
                      </div>
                      <div className="text-xs text-zinc-500 mt-1">
                        Add to In Person - Leader only · Brand felicitation · Speaker slot · Magazine feature · On-stage logo placement
                      </div>
                    </div>
                    <div className="font-display text-2xl text-[#FF2A00] shrink-0">+${BRAND_ADDON_PRICE}</div>
                  </div>
                </div>
              </div>

              <p className="mt-6 text-[11px] text-zinc-500">
                Prices in USD are for international delegates. Indian residents please
                register via the main <Link to="/#registration" className="underline">Registration section</Link>.
              </p>
            </div>

            <div className="col-span-12 lg:col-span-7">
              <div className="border border-zinc-200 p-6 md:p-10 bg-white relative">
                <div className="absolute top-0 right-0 bg-[#FF2A00] text-white px-3 py-1 overline text-[10px]">
                  / USD Checkout
                </div>

                {done ? (
                  <div data-testid="intl-pay-success" className="py-10 text-center">
                    <CheckCircle2 size={48} className="mx-auto text-[#002FA7]" />
                    <h3 className="font-display text-2xl uppercase tracking-tight mt-6">
                      Payment received.
                    </h3>
                    <p className="text-zinc-600 mt-3 max-w-md mx-auto">
                      Your delegate pass is confirmed. A receipt has been sent to your email.
                    </p>
                    <Link
                      to="/"
                      className="mt-8 inline-flex items-center gap-2 bg-black text-white px-6 py-3 text-xs font-bold tracking-[0.2em] uppercase hover:bg-[#002FA7] transition-colors"
                    >
                      Back to home
                    </Link>
                  </div>
                ) : (
                  <form
                    onSubmit={(e) => { e.preventDefault(); payNow(); }}
                    data-testid="intl-pay-form"
                    className="grid grid-cols-1 md:grid-cols-2 gap-5"
                  >
                    <Field label="Full Name" testid="intl-full_name" required value={form.full_name} onChange={onChange("full_name")} placeholder="Ada Lovelace" />
                    <Field label="Work Email" testid="intl-email" required type="email" value={form.email} onChange={onChange("email")} placeholder="ada@example.com" />
                    <Field label="Phone" testid="intl-phone" required value={form.phone} onChange={onChange("phone")} placeholder="+1 415 555 0100" />
                    <Field label="Organization" testid="intl-organization" required value={form.organization} onChange={onChange("organization")} placeholder="Acme Corp" />
                    <Field label="Role / Title" testid="intl-role" required value={form.role} onChange={onChange("role")} placeholder="CIO" className="md:col-span-2" />

                    <div className="md:col-span-2">
                      <label className="overline text-zinc-500 mb-2 block">Participation Option</label>
                      <select
                        data-testid="intl-pass-option"
                        required
                        value={form.pass_option}
                        onChange={onChange("pass_option")}
                        className="w-full bg-white border border-zinc-300 px-4 py-3 text-sm font-body focus:outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] transition-colors appearance-none cursor-pointer"
                      >
                        <option value="" disabled>Select a participation option…</option>
                        {PARTICIPATION_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label} — {usd(opt.price)}
                          </option>
                        ))}
                      </select>
                    </div>

                    {addonAllowed && (
                      <label
                        data-testid="intl-brand-addon"
                        className="md:col-span-2 flex items-start gap-3 border border-zinc-300 bg-white px-4 py-3 cursor-pointer hover:border-[#002FA7] transition-colors"
                      >
                        <input
                          type="checkbox"
                          data-testid="intl-brand-addon-checkbox"
                          checked={brandAddon}
                          onChange={(e) => setBrandAddon(e.target.checked)}
                          className="mt-1 accent-[#002FA7]"
                        />
                        <span className="text-sm">
                          <span className="font-display uppercase tracking-tight">Add Brand add-on — {usd(BRAND_ADDON_PRICE)}</span>
                          <span className="block text-xs text-zinc-500 mt-1">
                            20-25 min speaker slot, brand felicitation, magazine feature, on-stage logo placement and more.
                          </span>
                        </span>
                      </label>
                    )}

                    {total > 0 && (
                      <div data-testid="intl-order-summary" className="md:col-span-2 border border-black bg-zinc-50">
                        <div className="bg-black text-white px-5 py-2 overline text-[10px] tracking-[0.25em] flex items-center justify-between">
                          <span>/ Order Summary</span>
                          <span className="text-[#FF2A00]">USD · No GST</span>
                        </div>
                        <div className="p-5 flex justify-between items-baseline">
                          <span className="font-display text-base uppercase tracking-tight text-black">
                            Grand Total
                          </span>
                          <span data-testid="intl-total-value" className="font-display text-2xl text-[#002FA7]">
                            {usd(total)}
                          </span>
                        </div>
                      </div>
                    )}

                    <div className="md:col-span-2 mt-2">
                      <button
                        type="submit"
                        disabled={submitting || total <= 0}
                        data-testid="intl-pay-now"
                        className="w-full inline-flex items-center justify-center gap-2 bg-[#FF2A00] text-white px-7 py-4 text-xs font-bold tracking-[0.25em] uppercase hover:bg-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {submitting ? (
                          <>
                            <Loader2 size={16} className="animate-spin" /> Processing
                          </>
                        ) : (
                          <>
                            <CreditCard size={16} /> Pay Now →
                          </>
                        )}
                      </button>
                    </div>

                    <p className="md:col-span-2 text-[11px] text-zinc-500 mt-2">
                      By paying you agree to the BoT Summit Code of Conduct and Payment Terms.
                      Secure payments powered by Razorpay International.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="bg-black text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-8 flex flex-wrap items-center gap-6 overline text-zinc-500">
          <Link to="/" className="hover:text-white">Home</Link>
          <Link to="/payment-terms" className="hover:text-white">Payment Terms</Link>
          <Link to="/code-of-conduct" className="hover:text-white">Code of Conduct</Link>
          <Link to="/privacy" className="hover:text-white">Privacy</Link>
        </div>
      </footer>
    </div>
  );
}

const Field = ({ label, testid, className = "", ...rest }) => (
  <div className={className}>
    <label className="overline text-zinc-500 mb-2 block">{label}</label>
    <input
      data-testid={testid}
      className="w-full bg-white border border-zinc-300 px-4 py-3 text-sm font-body focus:outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] transition-colors"
      {...rest}
    />
  </div>
);
